<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";
import set from "lodash-es/set";

import { toast } from "@/common/utils/toast";

import type { ReportType } from "@/common/types/report";
import type { AvatarType } from "@/common/types/common";

import {
  fileAvatarFileShow,
  fileAvatarCreate,
  fileAvatarUpdate,
  fileAvatarDelete,
} from "@/common/api/rest/file/avatar";

// components
import {
  baseAccordion,
  baseAvatar,
  baseConfirm,
  baseInput,
  baseSelect,
  baseTextarea,
  baseTextEditor,
} from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["click:menu", "report:update", "report:reload"]);

const props = defineProps<{
  report: ReportType;
}>();

const payload = ref({
  front_cover: {
    visible: 0,
    font_family: "Verdana",
    align: "left",
    size: "16px",
    text: "",
  },
  header: {
    visible: 0,
    title: "",
    subtitle: "",
  },
  footer: {
    visible: 0,
    title: "",
    subtitle: "",
  },
  summary: {
    visible: 0,
    font_family: "Verdana",
    size: "16px",
  },
  name: "",
  description: "",
});

const selectedTab = ref<string>("content");
const selectedAcc = ref<string>("infos");

const mounted = ref(false);
const showDelAvatar = ref(false);

const avatar = computed(() => props.report?.avatar);

const showOptions = computed(() => [
  {
    option: "Sim",
    value: 1,
  },
  {
    option: "Não",
    value: 0,
  },
]);

const optionsFonts = computed(() => [
  { option: "Arial", value: "Arial" },
  { option: "Calibri", value: "Calibri" },
  { option: "Courier", value: "Courier" },
  { option: "Georgia", value: "Georgia" },
  { option: "Open Sans", value: "Open Sans" },
  { option: "Sans Serif", value: "sans-serif" },
  { option: "Times New Roman", value: "Times New Roman" },
  { option: "Verdana", value: "Verdana" },
]);

const optionsFontSize = computed(() => [
  { option: "10px", value: "10px" },
  { option: "11px", value: "11px" },
  { option: "12px", value: "12px" },
  { option: "13px", value: "13px" },
  { option: "14px", value: "14px" },
  { option: "15px", value: "15px" },
  { option: "16px", value: "16px" },
  { option: "17px", value: "17px" },
  { option: "18px", value: "18px" },
  { option: "20px", value: "20px" },
  { option: "22px", value: "22px" },
  { option: "24px", value: "24px" },
  { option: "26px", value: "26px" },
  { option: "28px", value: "28px" },
  { option: "30px", value: "30px" },
]);

const optionsAlign = computed(() => [
  { option: "Esquerda", value: "left" },
  { option: "Centro", value: "center" },
  { option: "Direita", value: "right" },
  { option: "Justificado", value: "justify" },
]);

const menuList = computed(() => {
  return [
    {
      key: "add",
      label: "Adicionar Bloco",
    },
  ];
});

const tabList = computed(() => [
  {
    key: "content",
    label: "Conteúdo",
  },
  {
    key: "report",
    label: "Relatório",
  },
]);

function toggleAccordion(key: string) {
  selectedAcc.value = selectedAcc.value === key ? "" : key;
}

function onUpdate(key: keyof typeof payload.value) {
  if (
    key === "header" ||
    key === "footer" ||
    key === "front_cover" ||
    key === "summary"
  ) {
    emit("report:update", {
      [key]: JSON.stringify(payload.value[key]),
    });

    return;
  }

  emit("report:update", {
    [key]: payload.value[key],
  });
}

function onUpdateEditor(
  key: keyof typeof payload.value,
  field: string,
  value: string,
) {
  set(payload.value, `${key}.${field}`, value);

  onUpdate(key);
}

function addAvatar(file: string) {
  fileAvatarCreate(props.report.account_id, {
    owner_id: props.report.report_id,
    type: "avatar",
    file: file,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        emit("report:reload");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      onUpdate("header");
    });
}

function updateAvatar(file: string) {
  if (!avatar.value) return;

  fileAvatarUpdate(props.report.account_id, avatar.value.avatar_id, { file })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        emit("report:reload");
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      onUpdate("header");
    });
}

function changeAvatar(file: string) {
  if (!avatar.value) {
    addAvatar(file);
  } else {
    updateAvatar(file);
  }
}

function delAvatar() {
  showDelAvatar.value = true;
}

function delAvatarConfirm() {
  if (!avatar.value) return;

  fileAvatarDelete(props.report.account_id, avatar.value.avatar_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        emit("report:reload");
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      showDelAvatar.value = false;
      onUpdate("header");
    });
}

const debounceUpdate = debounce(onUpdate, 500);

watch(
  () => props.report,
  (newReport) => {
    if (!newReport || mounted.value) return;

    payload.value = {
      ...payload.value,
      name: newReport.name || "",
      description: newReport.description || "",
      front_cover: {
        ...payload.value.front_cover,
        ...newReport.front_cover,
      },
      header: {
        ...payload.value.header,
        ...newReport.header,
      },
      footer: {
        ...payload.value.footer,
        ...newReport.footer,
      },
      summary: {
        ...payload.value.summary,
        ...newReport.summary,
      },
    };

    mounted.value = true;
  },
  { immediate: true },
);
</script>

<template>
  <div class="menu-empty">
    <div class="tabs">
      <template v-for="item in tabList" :key="item.key">
        <div
          class="tab"
          :class="{
            active: selectedTab === item.key,
          }"
          @click="selectedTab = item.key"
        >
          {{ item.label }}
        </div>
      </template>
    </div>

    <template v-if="selectedTab === 'content'">
      <div class="tab-content">
        <div class="menu-list">
          <template v-for="item in menuList" :key="item.key">
            <div class="menu" @click="emit('click:menu', item.key)">
              {{ item.label }}
            </div>
          </template>
        </div>
      </div>
    </template>

    <template v-else-if="selectedTab === 'report'">
      <div class="tab-report">
        <baseAccordion
          :open="selectedAcc === 'infos'"
          header-padding-block="0.5rem"
          header-padding-inline="0.75rem"
          padding-block="0"
          padding-inline="0"
          @open="toggleAccordion('infos')"
        >
          <template #title>
            <div class="flex flex-row gap-1 items-center">
              <span class="uppercase font-bold"> Relatório </span>
            </div>
          </template>

          <template #content>
            <div class="form">
              <base-input
                v-model="payload.name"
                :value="payload.name"
                background="#fff"
                @input="debounceUpdate('name')"
              >
                <template #label> Nome * </template>
              </base-input>

              <base-textarea
                v-model="payload.description"
                :value="payload.description"
                @input="debounceUpdate('description')"
              >
                <template #label> Descrição </template>
              </base-textarea>
            </div>
          </template>
        </baseAccordion>

        <!-- Front Cover -->
        <baseAccordion
          :open="selectedAcc === 'front_cover'"
          header-padding-block="0.5rem"
          header-padding-inline="0.75rem"
          padding-block="0"
          padding-inline="0"
          @open="toggleAccordion('front_cover')"
        >
          <template #title>
            <div class="flex flex-row gap-1 items-center">
              <span class="uppercase font-bold"> Capa </span>
            </div>
          </template>

          <template #content>
            <div class="form">
              <base-select
                v-model="payload.front_cover.visible"
                :value="payload.front_cover.visible"
                :options="showOptions"
                :is-empty="false"
                required
                @change="onUpdate('front_cover')"
              >
                <template #label> Exibir Capa? </template>
              </base-select>

              <base-select
                v-model="payload.front_cover.font_family"
                :value="payload.front_cover.font_family"
                height="32px"
                :is-empty="false"
                :options="optionsFonts"
                @change="onUpdate('front_cover')"
              >
                <template #label> Estilo de Fonte </template>
              </base-select>

              <base-select
                v-model="payload.front_cover.size"
                :value="payload.front_cover.size"
                height="32px"
                :is-empty="false"
                :options="optionsFontSize"
                @change="onUpdate('front_cover')"
              >
                <template #label> Tamanho </template>
              </base-select>

              <base-select
                v-model="payload.front_cover.align"
                :value="payload.front_cover.align"
                height="32px"
                :is-empty="false"
                :options="optionsAlign"
                @change="onUpdate('front_cover')"
              >
                <template #label> Alinhamento </template>
              </base-select>

              <base-text-editor
                v-model="payload.front_cover.text"
                :floating="false"
                :selected="true"
                :style="{
                  'font-size': '16px',
                }"
                @update="onUpdateEditor('front_cover', 'text', $event)"
              >
              </base-text-editor>
            </div>
          </template>
        </baseAccordion>

        <!-- Summary -->
        <baseAccordion
          :open="selectedAcc === 'summary'"
          header-padding-block="0.5rem"
          header-padding-inline="0.75rem"
          padding-block="0"
          padding-inline="0"
          @open="toggleAccordion('summary')"
        >
          <template #title>
            <div class="flex flex-row gap-1 items-center">
              <span class="uppercase font-bold"> Índice </span>
            </div>
          </template>

          <template #content>
            <div class="form">
              <base-select
                v-model="payload.summary.visible"
                :value="payload.summary.visible"
                :options="showOptions"
                :is-empty="false"
                required
                @change="onUpdate('summary')"
              >
                <template #label> Exibir Resumo? </template>
              </base-select>

              <base-select
                v-model="payload.summary.font_family"
                :value="payload.summary.font_family"
                height="32px"
                :is-empty="false"
                :options="optionsFonts"
                @change="onUpdate('summary')"
              >
                <template #label> Estilo de Fonte </template>
              </base-select>

              <base-select
                v-model="payload.summary.size"
                :value="payload.summary.size"
                height="32px"
                :is-empty="false"
                :options="optionsFontSize"
                @change="onUpdate('summary')"
              >
                <template #label> Tamanho da Fonte </template>
              </base-select>
            </div>
          </template>
        </baseAccordion>

        <baseAccordion
          :open="selectedAcc === 'header'"
          header-padding-block="0.5rem"
          header-padding-inline="0.75rem"
          padding-block="0"
          padding-inline="0"
          @open="toggleAccordion('header')"
        >
          <template #title>
            <div class="flex flex-row gap-1 items-center">
              <span class="uppercase font-bold"> Cabeçalho </span>
            </div>
          </template>

          <template #content>
            <div class="form">
              <div class="form-avatar">
                <base-avatar
                  :url="report?.avatar?.base64"
                  :is-edit="true"
                  background="#fff"
                  position="right"
                  size="6rem"
                  border-radius="0.5em"
                  @change="changeAvatar"
                  @del="delAvatar()"
                />
              </div>

              <base-select
                v-model="payload.header.visible"
                :value="payload.header.visible"
                :options="showOptions"
                :is-empty="false"
                required
                @change="onUpdate('header')"
              >
                <template #label> Exibir Cabeçalho? </template>
              </base-select>

              <base-textarea
                v-model="payload.header.title"
                :value="payload.header.title"
                @input="debounceUpdate('header')"
              >
                <template #label> Título </template>
              </base-textarea>

              <base-textarea
                v-model="payload.header.subtitle"
                :value="payload.header.subtitle"
                @input="debounceUpdate('header')"
              >
                <template #label> Subtítulo </template>
              </base-textarea>
            </div>
          </template>
        </baseAccordion>

        <baseAccordion
          :open="selectedAcc === 'footer'"
          header-padding-block="0.5rem"
          header-padding-inline="0.75rem"
          padding-block="0"
          padding-inline="0"
          @open="toggleAccordion('footer')"
        >
          <template #title>
            <div class="flex flex-row gap-1 items-center">
              <span class="uppercase font-bold"> Rodapé </span>
            </div>
          </template>

          <template #content>
            <div class="form">
              <base-select
                v-model="payload.footer.visible"
                :value="payload.footer.visible"
                :options="showOptions"
                :is-empty="false"
                required
                @change="onUpdate('footer')"
              >
                <template #label> Exibir Rodapé? </template>
              </base-select>

              <base-textarea
                v-model="payload.footer.title"
                :value="payload.footer.title"
                @input="debounceUpdate('footer')"
              >
                <template #label> Título </template>
              </base-textarea>

              <base-textarea
                v-model="payload.footer.subtitle"
                :value="payload.footer.subtitle"
                @input="debounceUpdate('footer')"
              >
                <template #label> Subtítulo </template>
              </base-textarea>
            </div>
          </template>
        </baseAccordion>
      </div>
    </template>

    <base-confirm
      :show="showDelAvatar"
      :title="t('common.labels.remove', { concat: t('common.labels.avatar') })"
      :message="
        t('common.texts.remove_message', { concat: t('common.labels.avatar') })
      "
      @cancel="showDelAvatar = false"
      @confirm="delAvatarConfirm()"
    />
  </div>
</template>

<style lang="scss" scoped>
.menu-empty {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;

  .tabs {
    display: flex;
    flex-direction: row;
    border-bottom: 1px solid #ccc;

    .tab {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      height: 40px;
      font-size: 14px;
      cursor: pointer;
      user-select: none;

      &.active {
        border-bottom: 2px solid #000;
      }
    }
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 1rem;
    overflow: auto;
    padding-inline: 15px;
    padding-block-start: 15px;
    padding-block-end: 20px;

    .menu-list {
      display: flex;
      flex-direction: column;

      .menu {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 40px;
        border-bottom: 1px solid #ccc;
        cursor: pointer;
        font-size: 14px;
        background: #fff;

        &:hover {
          background: #bdbdbd;
        }
      }
    }
  }

  .tab-report {
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding-top: 0.5rem;

    .form {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding-inline: 1rem;
      padding-block-start: 1rem;
      padding-block-end: 2rem;

      .form-avatar {
        display: flex;
        margin-block-end: 1.5rem;
        background: #fff;
        padding: 0.5rem;
        border-radius: 0.5rem;
      }

      .form-item {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        label {
          font-size: 0.875rem;
          font-weight: 500;
        }
      }
    }
  }

  :deep(.custom-quill) {
    min-height: 150px;
    background: #fff !important;
  }

  :deep(.ql-editor) {
    min-height: 150px;
    padding: 0.25rem;
  }

  :deep(.ql-container) {
    height: auto !important;
    min-height: 150px;
  }

  :deep(.custom-toolbar) {
    padding: 4px !important;
    gap: 0px !important;
  }

  :deep(.base-input .content) {
    background: #fff !important;
  }

  :deep(.base-textarea .content) {
    background: #fff !important;
  }

  :deep(.base-accordion .content) {
    background: transparent;
  }

  :deep(.base-accordion .header .font-bold) {
    font-weight: 400 !important;
    font-size: 0.875rem !important;
  }
}
</style>
